import { useState } from 'react'
import { ferry, money } from '../lib/ferry'
import { t } from '../lib/i18n'
import Icon from './Icon'

/**
 * Payment.
 *
 * ── This is a rehearsal and it says so ───────────────────────────────────────
 *
 * Evertec is the intended processor because the ferry already takes cards
 * through them, and using their existing merchant account is the whole reason
 * it is the right choice. That integration does not exist.
 *
 * What exists is the SHAPE — an idempotency key so a double tap charges once, a
 * provider that is allowed to fail, a booking that only becomes `paid` when the
 * charge is captured, and a receipt. Swapping the stub for Evertec is a
 * `payments.register()` call and nothing else.
 *
 * The banner at the top is not decoration. A payment screen that cannot be told
 * from a real one is how somebody ends up believing they have paid for a
 * crossing they have not, and finds out at the gate.
 *
 * No card fields. Not "not yet" — never: entering a card number into a form
 * that does not process cards teaches people to type card numbers into
 * anything that looks official.
 */
export default function Checkout({ bookingId, totalCents, reference, onPaid, onBack }: {
  bookingId: string
  totalCents: number
  reference: string
  onPaid: () => void
  onBack: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // Stable for the life of this screen, so a double tap is ONE charge — that is
  // the entire job of the key and it must not be regenerated per attempt.
  const [key] = useState(() => `pay_${bookingId}_${Date.now().toString(36)}`)

  async function pay(method: string) {
    setBusy(true); setError('')
    try {
      await ferry.pay(bookingId, { idempotencyKey: key, method })
      onPaid()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card checkout">
      <button className="back with-icon" onClick={onBack}><Icon name="back" size={16} />{t('pay.back')}</button>

      <div className="demo-banner">
        <Icon name="alert" size={18} />
        <div>
          <strong>{t('pay.demoTitle')}</strong>
          <p>{t('pay.demoBody')}</p>
        </div>
      </div>

      <h1>{t('pay.title')}</h1>

      <dl className="pass-facts">
        <div className="span-2">
          <dt>{t('pass.reference')}</dt>
          <dd className="reference">{reference}</dd>
        </div>
        <div className="span-2">
          <dt>{t('pay.amount')}</dt>
          <dd className="amount">{money(totalCents / 100)}</dd>
        </div>
      </dl>

      {error && <p className="error" role="alert">{error}</p>}

      <div className="pay-methods">
        <button className="primary with-icon big" disabled={busy} onClick={() => void pay('evertec')}>
          <Icon name="shield" size={18} />{busy ? t('pay.working') : t('pay.payCard')}
        </button>
        <button className="with-icon" disabled={busy} onClick={() => void pay('manual')}>
          <Icon name="ticket" size={16} />{t('pay.payCounter')}
        </button>
      </div>

      <p className="muted small">{t('pay.holdNote')}</p>
    </section>
  )
}
